import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * HelpQuickMenu — single floating "?" button that opens a compact bottom-
 * sheet (mobile) / popover (desktop) with four quick actions:
 *
 *   1. Replay Tour        — dispatches window event 'vs-help-replay-tour'
 *                           that route-specific TutorialHelpButton wrappers
 *                           listen for. (Each route's Tutorial component
 *                           can hook startTour to this event.)
 *   2. Send Feedback      — inline compact rating + message form, POSTs to
 *                           /api/feedback/general (cookie auth via global
 *                           interceptor).
 *   3. Email Support      — mailto:support@voxsmyth.com link.
 *   4. What's New         — navigates to /changelog (or a CHANGELOG page).
 *
 * Replaces the older `FloatingFeedbackButton` (full modal) with a smaller,
 * mobile-first interaction. Sized for ≤768px first.
 */
const HelpQuickMenu = () => {
  const [open, setOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [rating, setRating] = useState(5);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768,
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const hapt = (ms = 12) => {
    try { window.navigator?.vibrate && window.navigator.vibrate(ms); } catch (_) { /* noop */ }
  };

  const close = () => { setOpen(false); setShowFeedback(false); };

  const handleReplayTour = () => {
    hapt(15);
    close();
    window.dispatchEvent(new CustomEvent('vs-help-replay-tour'));
  };

  const handleEmailSupport = () => {
    hapt(15);
    close();
    window.location.href = 'mailto:support@voxsmyth.com?subject=VoxSmyth%20Support';
  };

  const handleWhatsNew = () => {
    hapt(15);
    close();
    window.location.href = '/changelog';
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/feedback/general`;
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          category: 'general',
          title: `Quick feedback (${rating}★)`,
          description: description.trim(),
          would_recommend: rating >= 4,
          page_url: window.location.href,
          browser_info: navigator.userAgent,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        setTimeout(() => {
          close();
          setSubmitted(false);
          setRating(5);
          setDescription('');
        }, 1600);
      } else {
        // Surface non-OK as a quiet inline error.
        console.error('[HelpQuickMenu] feedback POST failed', res.status);
      }
    } catch (err) {
      console.error('[HelpQuickMenu] feedback error', err);
    } finally {
      setSubmitting(false);
    }
  };

  const ACTIONS = [
    { id: 'replay-tour', label: 'Replay Tour', icon: '↻', onClick: handleReplayTour },
    { id: 'send-feedback', label: 'Send Feedback', icon: '✎', onClick: () => { hapt(15); setShowFeedback(true); } },
    { id: 'email-support', label: 'Email Support', icon: '✉', onClick: handleEmailSupport },
    { id: 'whats-new', label: "What's New", icon: '✨', onClick: handleWhatsNew },
  ];

  return (
    <>
      {/* Floating "?" button */}
      <button
        type="button"
        data-testid="help-quick-menu-button"
        className="vs-help-fab"
        onClick={() => { hapt(15); setOpen((o) => !o); }}
        aria-label="Help and feedback menu"
        style={{
          position: 'fixed',
          ...(isMobile
            ? { bottom: 'calc(env(safe-area-inset-bottom) + 80px)', right: 12 }
            : { bottom: 24, right: 24 }),
          zIndex: 9000,
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #D7B377, #DAA520)',
          border: 'none',
          color: '#1A202C',
          fontSize: '20px',
          fontWeight: 800,
          cursor: 'pointer',
          boxShadow: '0 6px 18px rgba(215,179,119,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          WebkitTapHighlightColor: 'transparent',
          transition: 'transform 0.2s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        ?
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              data-testid="help-quick-menu-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={close}
              style={{ position: 'fixed', inset: 0, background: 'rgba(7,11,20,0.55)', backdropFilter: 'blur(6px)', zIndex: 9100 }}
            />
            <motion.div
              data-testid="help-quick-menu"
              initial={isMobile ? { y: 200, opacity: 0 } : { scale: 0.92, opacity: 0 }}
              animate={isMobile ? { y: 0, opacity: 1 } : { scale: 1, opacity: 1 }}
              exit={isMobile ? { y: 200, opacity: 0 } : { scale: 0.92, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              style={{
                position: 'fixed',
                ...(isMobile
                  ? { left: 12, right: 12, bottom: 'calc(env(safe-area-inset-bottom) + 136px)' }
                  : { bottom: 80, right: 24, width: 280 }),
                zIndex: 9101,
                background: 'linear-gradient(180deg, #1E293B 0%, #0F172A 100%)',
                border: '1px solid rgba(215,179,119,0.32)',
                borderRadius: 18,
                padding: showFeedback ? 16 : 8,
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                fontFamily: 'Inter, sans-serif',
                color: '#F8FAFC',
                maxHeight: '70vh',
                overflowY: 'auto',
              }}
            >
              {isMobile && !showFeedback && (
                <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(215,179,119,0.35)', margin: '0 auto 10px' }} />
              )}

              {!showFeedback ? (
                <div role="menu" aria-label="Help menu">
                  {ACTIONS.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      role="menuitem"
                      data-testid={`help-action-${a.id}`}
                      onClick={a.onClick}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        width: '100%', minHeight: 48,
                        padding: '10px 14px',
                        background: 'transparent', border: 'none',
                        color: '#F8FAFC', fontSize: 15, fontWeight: 500,
                        textAlign: 'left', cursor: 'pointer',
                        borderRadius: 10,
                        WebkitTapHighlightColor: 'transparent',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(215,179,119,0.12)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: 'rgba(215,179,119,0.18)',
                        color: '#D7B377',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, flexShrink: 0,
                      }}>{a.icon}</span>
                      {a.label}
                    </button>
                  ))}
                </div>
              ) : submitted ? (
                <div style={{ textAlign: 'center', padding: '24px 8px' }}>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>✓</div>
                  <div style={{ fontFamily: 'DM Serif Display, serif', fontSize: 18, color: '#D7B377' }}>Thank you!</div>
                  <div style={{ fontSize: 13, color: '#94A3B8', marginTop: 4 }}>Your feedback has been sent.</div>
                </div>
              ) : (
                <form onSubmit={handleSubmitFeedback} data-testid="help-feedback-form">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <button type="button" onClick={() => setShowFeedback(false)} style={{
                      background: 'transparent', border: 'none', color: '#94A3B8',
                      fontSize: 13, cursor: 'pointer', padding: 4,
                    }}>← Back</button>
                    <div style={{ fontFamily: 'DM Serif Display, serif', fontSize: 17, color: '#F8FAFC' }}>Quick Feedback</div>
                    <span style={{ width: 38 }} aria-hidden="true" />
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, color: '#CBD5E0', marginBottom: 6, letterSpacing: '0.04em' }}>Rating</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`feedback-rating-${s}`}
                          onClick={() => { hapt(8); setRating(s); }}
                          style={{
                            flex: 1, minHeight: 38, padding: 0,
                            background: rating >= s ? 'linear-gradient(135deg, #D7B377, #DAA520)' : 'rgba(215,179,119,0.08)',
                            color: rating >= s ? '#0F172A' : '#94A3B8',
                            border: '1px solid rgba(215,179,119,0.25)',
                            borderRadius: 8, fontSize: 18, fontWeight: 700,
                            cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                          }}
                        >★</button>
                      ))}
                    </div>
                  </div>

                  <textarea
                    data-testid="feedback-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What's on your mind? (optional)"
                    rows={3}
                    maxLength={500}
                    style={{
                      width: '100%', padding: '10px 12px',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(215,179,119,0.22)',
                      borderRadius: 10, color: '#F8FAFC',
                      fontFamily: 'Inter, sans-serif', fontSize: 14,
                      resize: 'vertical', boxSizing: 'border-box',
                      marginBottom: 14,
                    }}
                  />

                  <button
                    type="submit"
                    data-testid="feedback-submit"
                    disabled={submitting}
                    style={{
                      width: '100%', minHeight: 44,
                      background: 'linear-gradient(135deg, #D7B377, #DAA520)',
                      color: '#0F172A',
                      border: 'none', borderRadius: 22,
                      fontWeight: 700, fontSize: 14, letterSpacing: '0.03em',
                      cursor: submitting ? 'default' : 'pointer',
                      opacity: submitting ? 0.6 : 1,
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    {submitting ? 'Sending…' : 'Send Feedback'}
                  </button>
                </form>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default HelpQuickMenu;
